import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  HourglassEmpty,
  CheckCircle,
  Block,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../components/AuthProvider';

export default function SellerPending() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sellerStatus, setSellerStatus] = useState<string | null>(null);
  const [storeName, setStoreName] = useState<string>('');

  useEffect(() => {
    loadSellerStatus();
  }, [user]);

  const loadSellerStatus = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);

      const { data: sellerProfile, error } = await supabase
        .from('seller_profile')
        .select('status, store_name')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error loading seller profile:', error);
        navigate('/register');
        return;
      }

      if (sellerProfile) {
        setSellerStatus(sellerProfile.status);
        setStoreName(sellerProfile.store_name);

        // active 상태면 seller 페이지로 리다이렉트
        if (sellerProfile.status === 'active') {
          navigate('/seller');
        }
      } else {
        // seller_profile이 없으면 등록 페이지로
        navigate('/register');
      }
    } catch (error) {
      console.error('Error in loadSellerStatus:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const getStatusIcon = () => {
    switch (sellerStatus) {
      case 'pending':
        return <HourglassEmpty sx={{ fontSize: 80, color: '#f39c12' }} />;
      case 'rejected':
        return <Cancel sx={{ fontSize: 80, color: '#e74c3c' }} />;
      case 'suspended':
        return <Block sx={{ fontSize: 80, color: '#e74c3c' }} />;
      case 'active':
        return <CheckCircle sx={{ fontSize: 80, color: '#10b981' }} />;
      default:
        return <HourglassEmpty sx={{ fontSize: 80, color: '#95a5a6' }} />;
    }
  };

  const getStatusMessage = () => {
    switch (sellerStatus) {
      case 'pending':
        return {
          title: t('seller.pending.statusPendingTitle'),
          description: t('seller.pending.statusPendingDesc'),
          color: '#f39c12',
        };
      case 'rejected':
        return {
          title: t('seller.pending.statusRejectedTitle'),
          description: t('seller.pending.statusRejectedDesc'),
          color: '#e74c3c',
        };
      case 'suspended':
        return {
          title: t('seller.pending.statusSuspendedTitle'),
          description: t('seller.pending.statusSuspendedDesc'),
          color: '#e74c3c',
        };
      default:
        return {
          title: t('seller.pending.statusCheckingTitle'),
          description: t('seller.pending.statusCheckingDesc'),
          color: '#95a5a6',
        };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const statusMessage = getStatusMessage();

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        bgcolor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: { xs: '100%', sm: 600 },
          maxWidth: 600,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 2px 16px 0 rgba(30, 42, 50, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          {/* 상태 아이콘 */}
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              bgcolor: `${statusMessage.color}20`,
              border: `2px solid ${statusMessage.color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            {getStatusIcon()}
          </Box>

          {/* 제목 */}
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#2c3e50' }}>
            {statusMessage.title}
          </Typography>

          {/* 스토어명 */}
          {storeName && (
            <Typography variant="body1" sx={{ mb: 2, color: '#7f8c8d', fontWeight: 600 }}>
              {storeName}
            </Typography>
          )}

          {/* 설명 */}
          <Typography variant="body1" sx={{ mb: 4, color: '#7f8c8d', fontSize: 16 }}>
            {statusMessage.description}
          </Typography>

          {/* 추가 정보 */}
          {sellerStatus === 'pending' && (
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>{t('seller.pending.approvalTime')}</strong> {t('seller.pending.approvalTimeValue')}
                <br />
                <strong>{t('seller.pending.approvalResult')}</strong> {t('seller.pending.approvalResultValue')}
              </Typography>
            </Alert>
          )}

          {sellerStatus === 'rejected' && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                {t('seller.pending.rejectedInfo')}
              </Typography>
            </Alert>
          )}

          {sellerStatus === 'suspended' && (
            <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                {t('seller.pending.suspendedInfo')}
              </Typography>
            </Alert>
          )}

          {/* 버튼들 */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => loadSellerStatus()}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 2,
                borderColor: '#bdc3c7',
                color: '#2c3e50',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#95a5a6',
                  bgcolor: '#ecf0f1',
                },
              }}
            >
              {t('seller.pending.refreshStatus')}
            </Button>
            <Button
              variant="contained"
              onClick={handleLogout}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 2,
                bgcolor: '#3498db',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#2980b9',
                },
              }}
            >
              {t('seller.pending.logoutBtn')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
